import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  DiscussionCommentResponse,
  EmojiLookup,
  UserReaction,
} from '../../request-discussion.model';
import { ApiResponse } from '../../../../../../common/models/api-response.model';
import { ENDPOINTS } from '../../../../../../common/constants/endpoints.constants';
import { IRequestDiscussionService } from '../request-discussion.interface';

@Injectable({
  providedIn: 'root',
})
export class VendorRequestDiscussionService implements IRequestDiscussionService {
  private readonly http = inject(HttpClient);

  addComment(
    flowId: string,
    body: { content: string; attachments: string[] }
  ): Observable<void> {
    const endpoint = ENDPOINTS.VendorPortal.Flow.Discussion.AddComment.replace(
      '{flow_id}',
      flowId
    );
    return this.http.post<void>(endpoint, body);
  }

  getComments(
    flowId: string,
    nextCursor = ''
  ): Observable<DiscussionCommentResponse> {
    let params = new HttpParams();
    if (nextCursor) {
      params = params.append('cursor', nextCursor);
    }
    const endpoint = ENDPOINTS.VendorPortal.Flow.Discussion.GetComments.replace(
      '{flow_id}',
      flowId
    );
    return this.http.get<DiscussionCommentResponse>(endpoint, { params });
  }

  markAsRead(flowId: string): Observable<void> {
    const endpoint = ENDPOINTS.VendorPortal.Flow.Discussion.MarkAsRead.replace(
      '{flowId}',
      flowId
    );
    return this.http.post<void>(endpoint, {});
  }

  storeReaction(
    flowId: string,
    commentId: string,
    emoji_slug: string
  ): Observable<void> {
    const endpoint =
      ENDPOINTS.VendorPortal.Flow.Discussion.StoreReaction.replace(
        '{flowId}',
        flowId
      ).replace('{commentId}', commentId);
    return this.http.post<void>(endpoint, { emoji_slug: emoji_slug });
  }

  deleteReaction(flowId: string, commentId: string): Observable<void> {
    const endpoint =
      ENDPOINTS.VendorPortal.Flow.Discussion.DeleteReaction.replace(
        '{flowId}',
        flowId
      ).replace('{commentId}', commentId);
    return this.http.delete<void>(endpoint);
  }

  getReactions(
    flowId: string,
    commentId: string,
    emojiSlug = ''
  ): Observable<ApiResponse<UserReaction>> {
    const endpoint =
      ENDPOINTS.VendorPortal.Flow.Discussion.GetReactions.replace(
        '{flowId}',
        flowId
      ).replace('{commentId}', commentId);
    let params = new HttpParams();
    if (emojiSlug) {
      params = params.append('filters[emoji_slug]', emojiSlug);
    }
    return this.http.get<ApiResponse<UserReaction>>(endpoint, { params });
  }

  updateComment(
    flowId: string,
    commentId: string,
    body: { content: string; attachments: string[] }
  ): Observable<void> {
    const endpoint =
      ENDPOINTS.VendorPortal.Flow.Discussion.UpdateComment.replace(
        '{flowId}',
        flowId
      ).replace('{commentId}', commentId);
    return this.http.put<void>(endpoint, body);
  }

  deleteComment(flowId: string, commentId: string): Observable<void> {
    const endpoint =
      ENDPOINTS.VendorPortal.Flow.Discussion.DeleteComment.replace(
        '{flowId}',
        flowId
      ).replace('{commentId}', commentId);
    return this.http.delete<void>(endpoint);
  }

  getEmojiLookup(): Observable<ApiResponse<EmojiLookup>> {
    const endpoint = ENDPOINTS.Lookup.Emoji;
    return this.http.get<ApiResponse<EmojiLookup>>(endpoint);
  }

  getAttachments(
    flowId: string,
    nextCursor = ''
  ): Observable<DiscussionCommentResponse> {
    let params = new HttpParams();
    if (nextCursor) {
      params = params.append('cursor', nextCursor);
    }
    const endpoint =
      ENDPOINTS.VendorPortal.Flow.Discussion.GetAttachments.replace(
        '{flowId}',
        flowId
      );
    return this.http.get<DiscussionCommentResponse>(endpoint, { params });
  }
}
